// ABOUTME: The React Query hooks behind the feed, one listing and the saved preferences: they wait for the Auth0 token,
// ABOUTME: ask in the app's language, and key the cache on both plus the criteria so a changed filter is a new list.
import { useCallback, useEffect, useRef } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from 'react-query';
import { USE_APPLICATIONS_QUERY_KEY } from 'modules/applications';
import { cvApi } from 'modules/cv-builder';
import { USE_FEED_QUERY_KEY } from 'modules/feed/constants';
import { getApiLocale } from 'modules/i18n';
import { useAccessToken } from 'utils/auth';
import { jobListingsApi } from './client';
import {
  APPLY_MUTATION_KEY,
  SAVE_SEARCH_PREFERENCES_MUTATION_KEY,
  USE_APPLICATION_JOB_QUERY_KEY,
  USE_ASSIGNMENT_JOB_QUERY_KEY,
  USE_COMMUTE_QUERY_KEY,
  USE_FILTER_OPTIONS_QUERY_KEY,
  USE_JOB_LISTING_QUERY_KEY,
  USE_JOB_LISTINGS_QUERY_KEY,
  USE_PRO_CV_QUERY_KEY,
  USE_SEARCH_PREFERENCES_QUERY_KEY,
  USE_WEEK_FIT_QUERY_KEY
} from './constants';
import {
  ApplyAnswer,
  ApplyRequest,
  CommuteAnswer,
  FilterOptions,
  JobListing,
  JobListingSearchCriteria,
  JobListingsPage,
  JobPage,
  ListingEngagementAction,
  ListingEngagementSource,
  SearchPreferences,
  TravelMode,
  WeekFitAnswer,
  WeekFitRequest
} from './types';

/**
 * The feed, a page at a time. Keyed on the criteria as text, so two renders with equal filters share one list and a
 * changed filter starts another. Not retried: the 404 that says PRO_JOB_LISTING is off is pro-core's settled answer.
 */
export const useJobListingsQuery = (criteria: JobListingSearchCriteria) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useInfiniteQuery<JobListingsPage>(
    [USE_JOB_LISTINGS_QUERY_KEY, token, language, JSON.stringify(criteria)],
    // `token!` is safe: the query only runs once `enabled` says there is one.
    ({ pageParam = 0 }) =>
      jobListingsApi.searchJobListings(
        token!,
        language,
        criteria,
        pageParam as number
      ),
    {
      enabled: !!token,
      retry: false,
      getNextPageParam: last => {
        const { page, totalPages } = last.meta.pagination;
        return page + 1 < totalPages ? page + 1 : undefined;
      }
    }
  );
};

/** One listing. Not retried: a 400 not-found here is pro-core's answer about this refId, not a blip. */
export const useJobListingQuery = (refId: string | undefined) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<JobListing>(
    [USE_JOB_LISTING_QUERY_KEY, token, language, refId],
    () => jobListingsApi.fetchJobListing(token!, language, refId!),
    { enabled: !!token && !!refId, retry: false }
  );
};

/**
 * PJL: the job of an application or of my job, for its job page. Not retried: a 404 is the flags being off and any
 * failure leaves the page as it was, so a second ask would only keep the skeleton longer.
 */
export const useJobPageQuery = (
  of: 'application' | 'assignment',
  ref: string | undefined
) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<JobPage>(
    [
      of === 'application'
        ? USE_APPLICATION_JOB_QUERY_KEY
        : USE_ASSIGNMENT_JOB_QUERY_KEY,
      token,
      language,
      ref
    ],
    () =>
      of === 'application'
        ? jobListingsApi.fetchApplicationJob(token!, language, ref!)
        : jobListingsApi.fetchAssignmentJob(token!, language, ref!),
    { enabled: !!token && !!ref, retry: false }
  );
};

export const useSearchPreferencesQuery = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<SearchPreferences>(
    [USE_SEARCH_PREFERENCES_QUERY_KEY, token, language],
    () => jobListingsApi.fetchSearchPreferences(token!, language),
    { enabled: !!token, retry: false }
  );
};

/**
 * The occupations and companies the filter sheet offers. Named in the request's language, so the language is part
 * of the key. Not retried: a 404 is the feed being off, or a pro-core without the route yet.
 */
export const useFilterOptionsQuery = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<FilterOptions>(
    [USE_FILTER_OPTIONS_QUERY_KEY, token, language],
    () => jobListingsApi.fetchFilterOptions(token!, language),
    { enabled: !!token, retry: false }
  );
};

/** What a change to the saved preferences is: the next document, made from the one stored right now. */
export type SearchPreferencesUpdate = (
  current: SearchPreferences
) => SearchPreferences;

/** A save asked for before the preferences were read: there is nothing to build the next document from. */
export class SearchPreferencesNotLoadedError extends Error {
  constructor() {
    super('search preferences not loaded yet');
  }
}

/**
 * Replaces the document whole, built from the cached one at the moment the request goes out, not from what a render
 * saw: a refetch in flight is cancelled first so it cannot land a stale copy afterwards, and the answer (the document
 * as stored, with the refIds the server minted) becomes the cache, so the next save starts from it. Callers serialise
 * the saves; see `useSearchPreferences`.
 */
export const useSaveSearchPreferencesMutation = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const queryClient = useQueryClient();
  const key = [USE_SEARCH_PREFERENCES_QUERY_KEY, token, language];

  return useMutation<SearchPreferences, unknown, SearchPreferencesUpdate>(
    [SAVE_SEARCH_PREFERENCES_MUTATION_KEY, token],
    async update => {
      await queryClient.cancelQueries(key);
      const current = queryClient.getQueryData<SearchPreferences>(key);
      if (!current) throw new SearchPreferencesNotLoadedError();
      const stored = await jobListingsApi.saveSearchPreferences(
        token!,
        language,
        update(current)
      );
      queryClient.setQueryData(key, stored);
      return stored;
    }
  );
};

/**
 * The apply. On success the feed and the listing are read again so the card and the detail show `myApplication`, and
 * the applications so the tracker shows the new row. It does not seed the application's detail: the answer is a refId
 * and not the application, unlike the withdrawal, so there is nothing honest to seed with.
 */
export const useApplyToJobListingMutation = (jobListingRefId: string) => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const queryClient = useQueryClient();

  return useMutation<ApplyAnswer, unknown, ApplyExtras | void>(
    [APPLY_MUTATION_KEY, token, jobListingRefId],
    // Without extras the body is exactly today's { jobListingRefId }; the PJL apply flow adds the week it collected.
    extras =>
      jobListingsApi.applyToJobListing(token!, language, {
        jobListingRefId,
        ...(extras ?? {})
      }),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries([USE_JOB_LISTINGS_QUERY_KEY]),
          queryClient.invalidateQueries([USE_JOB_LISTING_QUERY_KEY]),
          queryClient.invalidateQueries([USE_APPLICATIONS_QUERY_KEY]),
          queryClient.invalidateQueries([USE_FEED_QUERY_KEY])
        ]);
      }
    }
  );
};

/** What the PJL apply flow sends besides the listing: everything of ApplyRequest but the refId the hook already has. */
export type ApplyExtras = Omit<ApplyRequest, 'jobListingRefId'>;

/**
 * The commute to a listing's places, asked only once the talent asks (`enabled`): pro-core calls a paid routing
 * provider for it, per travel mode, so it is never read ahead of a tap. Not retried: one failure is shown as such.
 */
export const useCommuteQuery = (
  refId: string,
  mode: TravelMode,
  enabled: boolean
) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<CommuteAnswer>(
    [USE_COMMUTE_QUERY_KEY, token, refId, mode],
    () => jobListingsApi.fetchCommute(token!, language, refId, mode),
    { enabled: !!token && enabled, retry: false, staleTime: Infinity }
  );
};

interface EngagementStep {
  refId: string;
  action: ListingEngagementAction;
  source: ListingEngagementSource;
}

/**
 * One step of the apply funnel; fire and forget, a lost step is pro-core's count missing one, nothing the talent sees.
 * Each caller's token arrives a moment after it mounts, and the apply sheet records its first step on mount: a step
 * asked for before the token waits for it, because a request without one answers 401 and that logs the talent out.
 */
export const useRecordEngagementMutation = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const waiting = useRef<
    { step: EngagementStep; options?: { onError?: () => void } }[]
  >([]);

  const { mutate: send } = useMutation<void, unknown, EngagementStep>(
    ({ refId, action, source }) =>
      jobListingsApi.recordEngagement(token!, language, refId, action, source)
  );

  useEffect(() => {
    if (!token) return;
    waiting.current
      .splice(0)
      .forEach(({ step, options }) => send(step, options));
  }, [token, send]);

  const mutate = useCallback(
    (step: EngagementStep, options?: { onError?: () => void }) => {
      if (!token) {
        waiting.current.push({ step, options });
        return;
      }
      send(step, options);
    },
    [token, send]
  );

  return { mutate };
};

/**
 * The week check of one listing (POST …/weekFit). Keyed on the days sent, so editing a day asks again and going back to
 * the profile's week reads from cache. Kept until the apply sheet closes; not retried.
 */
export const useWeekFitQuery = (
  refId: string,
  request: WeekFitRequest,
  enabled: boolean
) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<WeekFitAnswer>(
    [USE_WEEK_FIT_QUERY_KEY, token, language, refId, JSON.stringify(request)],
    () => jobListingsApi.fetchWeekFit(token!, language, refId, request),
    { enabled: !!token && enabled, retry: false, keepPreviousData: true }
  );
};

/** Whether this talent has a PRO CV: pro-core answers the CV or `error.cv.not-found`, which cvApi.fetchCv turns into null. */
export const useProCvQuery = () => {
  const { token } = useAccessToken();

  return useQuery([USE_PRO_CV_QUERY_KEY, token], () => cvApi.fetchCv(token), {
    enabled: !!token,
    retry: false
  });
};
